import { useEffect, useState } from "react";
import { Lock, LockOpen, GearSix } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import api, { apiErrorMessage, setTimesheetSource } from "@/lib/api";

const OPTS = [["strata", "Strata Data"], ["sheet", "Google Sheet Data"], ["both", "Both"]];
const KEY = "ts_dashboard_source";

/** Dashboard data source selector. Applies to every Timesheet/payroll request via the api interceptor. */
export default function SourcePill({ onChange }) {
  const [cfg, setCfg] = useState(null);
  const [val, setVal] = useState(null);
  const [rec, setRec] = useState(null);

  const load = async () => {
    const { data } = await api.get("/timesheet/source");
    setCfg(data);
    const saved = localStorage.getItem(KEY);
    const v = data.locked && !data.is_super_admin ? "strata" : (OPTS.some(([k]) => k === saved) ? saved : data.default);
    setVal(v);
    setTimesheetSource(v);
    api.get("/timesheet/source/reconcile").then((r) => setRec(r.data)).catch(() => {});
    return v;
  };
  useEffect(() => { load().then((v) => onChange?.(v)).catch(() => {}); return () => setTimesheetSource(null); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (v) => {
    if (v === val) return;
    setVal(v);
    localStorage.setItem(KEY, v);
    setTimesheetSource(v);
    onChange?.(v);
  };
  const save = async (body, msg) => {
    try { await api.put("/timesheet/source", body); toast.success(msg); await load(); onChange?.(val); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  if (!cfg || !val) return null;
  const locked = cfg.locked && !cfg.is_super_admin;
  return (
    <div className="flex items-center gap-1.5" data-testid="ts-source">
      <div className="inline-flex items-center rounded-full border border-[#f59e0b]/40 bg-[#f59e0b]/10 p-0.5" title={rec ? `Sheet ${rec.sheet} · Strata ${rec.strata} · Manual ${rec.manual} · Both ${rec.both} (matched duplicates ${rec.matched_duplicates}, same-day conflicts ${rec.conflicts})` : ""}>
        {OPTS.map(([k, label]) => {
          const off = locked && k !== "strata";
          return (
            <button key={k} type="button" disabled={off} onClick={() => pick(k)} aria-pressed={val === k}
              className={`h-7 px-3 rounded-full text-[11px] font-semibold transition-colors ${val === k ? "bg-[#f59e0b] text-[#ffffff]" : off ? "text-[#c9a25a]/50 cursor-not-allowed" : "text-[#d97706] hover:bg-[#f59e0b]/15"}`}
              data-testid={`ts-source-${k}`}>{label}</button>
          );
        })}
      </div>
      {cfg.locked && <span className="inline-flex items-center gap-1 text-[11px] text-[#d97706]" data-testid="ts-source-locked"><Lock size={12} weight="bold" />{locked ? "Locked to Strata by a super admin" : "Locked"}</span>}
      {cfg.is_super_admin && (
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className="h-7 w-7 inline-flex items-center justify-center rounded-full text-[#d97706] hover:bg-[#f59e0b]/15" aria-label="Source settings" data-testid="ts-source-settings"><GearSix size={14} weight="bold" /></button>
          </PopoverTrigger>
          <PopoverContent className="w-72 text-sm space-y-3" data-testid="ts-source-settings-panel">
            <div className="font-semibold">Dashboard source settings</div>
            <div className="text-xs text-muted-foreground">Organization default: <b>{OPTS.find(([k]) => k === cfg.default)?.[1]}</b>{cfg.changed_by ? ` · by ${cfg.changed_by}` : ""}</div>
            <button type="button" disabled={cfg.locked || cfg.default === val} onClick={() => save({ default: val, locked: false }, "Default source saved")}
              className="w-full h-8 rounded-md border text-xs font-semibold disabled:opacity-40" data-testid="ts-source-set-default">Make “{OPTS.find(([k]) => k === val)?.[1]}” the default</button>
            <label className="flex items-start gap-2 text-xs">
              <Switch checked={cfg.locked} onCheckedChange={(on) => save({ default: on ? "strata" : cfg.default, locked: on }, on ? "Dashboard locked to Strata Data" : "Dashboard unlocked")} data-testid="ts-source-lock-switch" />
              <span>{cfg.locked ? <LockOpen size={12} className="inline mr-1" /> : <Lock size={12} className="inline mr-1" />}Lock Dashboard Source to Strata Data. Others can't switch away. Google Sheet data and syncing are kept.</span>
            </label>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
